import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import {
  disableQuestionnaireForClassroom,
  enableQuestionnaireForClassroom,
  getQuestionnaireClassroomStatus,
  listQuestionnaires,
} from "../../api/endpoints/questionnaires";

/**
 * Painel de ativação de questionário por turma (extensão da tela Avaliações).
 * Não altera fluxo de notas — apenas gerencia liberação do questionário no app.
 */
export function QuestionnaireClassroomPanel({ turmaId, classroomName, userLevel }) {
  const [questionnaires, setQuestionnaires] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const canManage = ["admin", "coordinator"].includes(
    String(userLevel || "").toLowerCase()
  );

  const loadQuestionnaires = useCallback(async () => {
    try {
      const res = await listQuestionnaires({ status: "ACTIVE" });
      const items = res.questionnaires || [];
      setQuestionnaires(items);
      if (items.length > 0 && !selectedId) {
        setSelectedId(String(items[0].id));
      }
    } catch (e) {
      setError("Não foi possível carregar os questionários.");
    }
  }, [selectedId]);

  const loadStatus = useCallback(async () => {
    if (!turmaId || !selectedId) return;
    try {
      const res = await getQuestionnaireClassroomStatus(selectedId, turmaId);
      setEnabled(Boolean(res.enabled));
    } catch {
      setEnabled(false);
    }
  }, [turmaId, selectedId]);

  useEffect(() => {
    if (canManage) {
      loadQuestionnaires();
    }
  }, [canManage, loadQuestionnaires]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  if (!canManage || !turmaId) {
    return null;
  }

  const handleEnable = async () => {
    if (!selectedId) return;
    setLoading(true);
    setMessage("");
    setError("");
    try {
      await enableQuestionnaireForClassroom(selectedId, turmaId, classroomName);
      setEnabled(true);
      setMessage("Questionário ativado para todos os alunos desta turma.");
    } catch (e) {
      setError(e?.response?.data?.message || "Erro ao ativar questionário.");
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    if (!selectedId) return;
    setLoading(true);
    setMessage("");
    setError("");
    try {
      await disableQuestionnaireForClassroom(selectedId, turmaId);
      setEnabled(false);
      setMessage("Questionário desativado para esta turma.");
    } catch (e) {
      setError(e?.response?.data?.message || "Erro ao desativar questionário.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        backgroundColor: "#f8fbff",
      }}
    >
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        Questionários (app do aluno)
      </Typography>

      {questionnaires.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Nenhum questionário ativo disponível.
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
          <FormControl size="small" sx={{ minWidth: 280 }}>
            <InputLabel id="questionnaire-select-label">Questionário</InputLabel>
            <Select
              labelId="questionnaire-select-label"
              label="Questionário"
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
            >
              {questionnaires.map((q) => (
                <MenuItem key={q.id} value={String(q.id)}>
                  {q.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Chip
            label={enabled ? "Ativo nesta turma" : "Inativo nesta turma"}
            color={enabled ? "success" : "default"}
            variant={enabled ? "filled" : "outlined"}
          />

          <Button
            variant="contained"
            color="primary"
            disabled={loading || enabled || !selectedId}
            onClick={handleEnable}
          >
            Ativar turma
          </Button>

          <Button
            variant="outlined"
            color="warning"
            disabled={loading || !enabled || !selectedId}
            onClick={handleDisable}
          >
            Desativar
          </Button>
        </Box>
      )}

      {message && (
        <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
          {message}
        </Typography>
      )}
      {error && (
        <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}
